// Upload the client-approved strategy (build-document round 1, Edward
// 2026-11-04). The client's approved Word file becomes the approval of
// record; its copy goes into the build document word for word. Two steps:
// "Check the document" reads it and lists what the PM should know (nothing
// is saved), then "Record the client's approval" — which needs the PM's
// confirmation when there are warnings. Backend: routers/build_doc.py,
// services/approved_doc.py.
import React, { useState } from 'react';
import axios from 'axios';
import CollapsibleSection from '../../components/CollapsibleSection';
import FileDropZone from '../../components/FileDropZone';
import { fmtSize } from '../../utils/ciqAssets';
import {
  FONTS, INK, NEUTRAL, STATE, RADII, card, displayFont,
  buttonDisabled, buttonSmall, buttonPrimarySmall,
} from '../../utils/wizardTheme';

const API = process.env.REACT_APP_API_URL;

const SEVERITY = {
  block: { icon: '⛔', fg: STATE.danger.text, bg: STATE.danger.tint, border: STATE.danger.border },
  warn: { icon: '⚠', fg: STATE.gate.deep, bg: STATE.gate.bg, border: STATE.gate.border },
  info: { icon: 'ℹ', fg: INK.body, bg: NEUTRAL.band, border: NEUTRAL.borderSoft },
};

function Problem({ p }) {
  const s = SEVERITY[p.severity] || SEVERITY.info;
  return (
    <div style={{ display: 'flex', gap: '10px', background: s.bg, border: `1px solid ${s.border}`,
      borderRadius: RADII.small, padding: '8px 12px' }}>
      <span aria-hidden="true" style={{ color: s.fg, fontSize: '13px' }}>{s.icon}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '12.5px', color: s.fg }}>{p.title}</div>
        <div style={{ fontSize: '12px', color: INK.body, lineHeight: 1.5 }}>{p.message}</div>
        {(p.detail || []).length > 0 && (
          <ul style={{ margin: '4px 0 0', paddingLeft: '18px', fontSize: '11.5px', color: INK.secondary }}>
            {p.detail.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

// The comparison with ContentIQ’s copy, grouped by where it sits.
export function ChangeList({ changes }) {
  if (!changes?.compared) return null;
  const groups = [];
  const add = (where, entry) => {
    let g = groups.find(x => x.where === where);
    if (!g) { g = { where, entries: [] }; groups.push(g); }
    g.entries.push(entry);
  };
  (changes.items || []).forEach(i => add(i.where || 'Copy', i));
  (changes.new_lines || []).forEach(n => add(n.where || 'Copy', { kind: 'new', after: n.text }));
  const counts = `${changes.unchanged} the same · ${changes.changed} changed · ${changes.removed} not in the document · ${changes.new} new`;
  if (!groups.length) {
    return <div style={{ fontSize: '12.5px', color: INK.body }}>Compared with ContentIQ’s strategy copy: {counts}</div>;
  }
  return (
    <CollapsibleSection label={`Compared with ContentIQ’s strategy copy: ${counts}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '280px', overflowY: 'auto' }}>
        {groups.map(g => (
          <div key={g.where}>
            <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase',
              color: INK.muted, marginBottom: '3px' }}>{g.where}</div>
            {g.entries.map((e, i) => (
              <div key={i} style={{ fontSize: '12px', lineHeight: 1.5, padding: '2px 0',
                borderTop: i ? `1px solid ${NEUTRAL.borderSoft}` : 'none' }}>
                {e.kind === 'changed' && (
                  <>
                    <span style={{ color: INK.muted, textDecoration: 'line-through' }}>{e.before}</span><br />
                    <span style={{ color: INK.primary }}>{e.after}</span>
                  </>
                )}
                {e.kind === 'removed' && (
                  <span style={{ color: STATE.danger.text }}>
                    Not in the document: <span style={{ textDecoration: 'line-through' }}>{e.before}</span>
                  </span>
                )}
                {e.kind === 'new' && <span style={{ color: STATE.done.text }}>New: {e.after}</span>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </CollapsibleSection>
  );
}

export default function ApprovedDocDialog({ projectId, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [review, setReview] = useState(null);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const form = (extra = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
    return fd;
  };
  const detailText = (e, fallback) => {
    const d = e.response?.data?.detail;
    return typeof d === 'string' ? d : d?.message || fallback;
  };

  const check = async () => {
    if (!file) { setErr('Pick the client-approved .docx first.'); return; }
    setBusy(true); setErr(''); setAck(false);
    try {
      const r = await axios.post(`${API}/contentiq/projects/${projectId}/strategy/approved-doc/review`,
        form(), { timeout: 120000 });
      setReview(r.data);
    } catch (e) {
      setReview(null);
      setErr(detailText(e, 'Could not read the document.'));
    } finally { setBusy(false); }
  };

  const record = async () => {
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/contentiq/projects/${projectId}/strategy/approved-doc`,
        form({ acknowledged: ack ? 'true' : 'false' }), { timeout: 120000 });
      if (onDone) onDone();
      onClose();
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.detail?.review) {
        setReview(e.response.data.detail.review);
        setErr('Confirm the warnings above first.');
      } else {
        setErr(detailText(e, 'Could not record the approval.'));
      }
    } finally { setBusy(false); }
  };

  const problems = review?.problems || [];
  const order = { block: 0, warn: 1, info: 2 };
  const sorted = [...problems].sort((a, b) => (order[a.severity] ?? 3) - (order[b.severity] ?? 3));
  const canRecord = review && review.can_apply && (!review.needs_confirm || ack) && !busy;

  return (
    <div role="dialog" aria-modal="true" aria-label="Upload the client-approved strategy"
      style={{ position: 'fixed', inset: 0, background: 'rgba(19,24,67,0.35)', zIndex: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ ...card, width: 'min(640px, 94vw)', maxHeight: '90vh', overflowY: 'auto',
        padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '12px', fontFamily: FONTS.body }}>
        <div style={{ ...displayFont('16px', 650) }}>Upload the client-approved strategy</div>
        <div style={{ fontSize: '12.5px', color: INK.body, lineHeight: 1.55 }}>
          Use the Word file the client approved, with every change in it. It becomes the approval
          of record, and its copy goes to the build team word for word. ContentIQ’s own strategy
          stays as it is.
        </div>
        <FileDropZone multiple={false} accept=".docx" disabled={busy}
          ariaLabel="Client-approved strategy document"
          label={file ? file.name : 'Drop the approved Word document here, or click to choose it'}
          hint={file ? `${fmtSize(file.size)} — click to choose a different file` : '.docx, up to 10 MB'}
          onFiles={files => { setFile(files[0] || null); setReview(null); setErr(''); setAck(false); }} />

        {review && (
          <>
            <div style={{ fontSize: '12.5px', color: INK.primary }}>
              <b>{review.title || review.filename}</b>
              <span style={{ color: INK.secondary }}>
                {' · '}{review.chapter_found ? 'copy section found' : 'no copy section found'}
              </span>
            </div>
            {sorted.length === 0 && (
              <div style={{ fontSize: '12.5px', color: STATE.done.text }}>✓ Nothing to flag.</div>
            )}
            {sorted.map(p => <Problem key={p.code} p={p} />)}
            <ChangeList changes={review.changes} />
            {review.needs_confirm && review.can_apply && (
              <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '12.5px', color: INK.body }}>
                <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} style={{ marginTop: '2px' }} />
                I’ve checked the warnings above. This is the version the client approved.
              </label>
            )}
          </>
        )}

        {err && <div role="alert" style={{ color: STATE.danger.text, fontSize: '12.5px' }}>{err}</div>}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button onClick={onClose} disabled={busy} style={buttonSmall}>Cancel</button>
          {!review ? (
            <button onClick={check} disabled={busy || !file}
              style={{ ...buttonPrimarySmall(), ...(busy || !file ? buttonDisabled : {}) }}>
              {busy ? '⏳ Reading…' : 'Check the document'}
            </button>
          ) : (
            <button onClick={record} disabled={!canRecord}
              style={{ ...buttonPrimarySmall(STATE.done.main), ...(!canRecord ? buttonDisabled : {}) }}>
              {busy ? '⏳ Recording…' : '✓ Record the client’s approval'}
            </button>
          )}
        </div>
        <div style={{ fontSize: '11px', color: INK.muted }}>Replacing it later keeps the earlier file.</div>
      </div>
    </div>
  );
}
